import { FC } from "react"
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material"
import { ClientSelectRaw } from "../chains/executorInputs"
import {
  LaunchTargetState,
  ageFilter,
  switchLaunchExecutor,
} from "./launchTarget"

interface Props {
  value: LaunchTargetState
  onChange: (value: LaunchTargetState) => void
  /** Omitted options retain server-authored template defaults, not one/client. */
  inheritDefaults?: boolean
}

export const LaunchTargetFields: FC<Props> = ({
  value,
  onChange,
  inheritDefaults = false,
}) => {
  const update = (over: Partial<LaunchTargetState>) =>
    onChange({ ...value, ...over })
  return (
    <Stack spacing={2}>
      <RadioGroup
        row
        value={value.executor}
        onChange={(e) =>
          onChange(
            switchLaunchExecutor(
              value,
              e.target.value as LaunchTargetState["executor"],
            ),
          )
        }
      >
        <FormControlLabel value="client" control={<Radio />} label="Клиент" />
        <FormControlLabel value="role" control={<Radio />} label="Роль" />
      </RadioGroup>
      <ClientSelectRaw
        executor={value.executor}
        clientId={value.clientId ?? ""}
        role={value.role ?? ""}
        onClientChange={(clientId) => update({ clientId })}
        onRoleChange={(role) => update({ role })}
      />
      {value.executor === "role" && (
        <>
          <TextField
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            label="Режим роли"
            value={value.roleMode ?? (inheritDefaults ? "" : "one")}
            onChange={(e) =>
              update({
                roleMode: (e.target.value ||
                  undefined) as LaunchTargetState["roleMode"],
              })
            }
          >
            {inheritDefaults && <option value="">По умолчанию</option>}
            <option value="one">one — один исполнитель</option>
            <option value="all">all — все подходящие</option>
          </TextField>
          <TextField
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            label="Единица выбора"
            value={value.targetUnit ?? (inheritDefaults ? "" : "client")}
            onChange={(e) =>
              update({
                targetUnit: (e.target.value ||
                  undefined) as LaunchTargetState["targetUnit"],
              })
            }
          >
            {inheritDefaults && <option value="">По умолчанию</option>}
            <option value="client">client — сессия</option>
            <option value="account">account — аккаунт</option>
          </TextField>
        </>
      )}
      <TextField
        select
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        label="Фильтр работы"
        value={value.targetFilter ?? ""}
        onChange={(e) =>
          update({
            targetFilter: e.target.value as LaunchTargetState["targetFilter"],
            maxAgeDays: undefined,
          })
        }
      >
        <option value="">
          {inheritDefaults ? "По умолчанию" : "Без фильтра"}
        </option>
        <option value="pendingJoinRequests">pendingJoinRequests</option>
        <option value="noPendingJoinRequests">noPendingJoinRequests</option>
        <option value="unmatchedDialogs">unmatchedDialogs</option>
        <option value="knownMemberships">knownMemberships</option>
      </TextField>
      {ageFilter(value.targetFilter) && (
        <TextField
          label="maxAgeDays"
          type="number"
          inputProps={{ step: "any", min: 0 }}
          value={value.maxAgeDays ?? ""}
          placeholder="14"
          helperText="Положительное число дней, по умолчанию 14; дробные значения допустимы"
          onChange={(e) => update({ maxAgeDays: e.target.value })}
        />
      )}
    </Stack>
  )
}
